import { CARD_ACTIVATABLE_STATES, PAGE_SIZE_DEFAULT, type PrintBatchDetail, type PrintBatchListItem } from "@seagames/contracts";
import { type ColumnDef, DataTable, IconButton, Icons, KeysetPager, Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { BatchFlowTracker, batchFlow } from "../../components/e-accreditation/batch-flow";
import { CardGeneratorPanel } from "../../components/e-accreditation/card-generator-panel";
import { BATCH_PAGE_SIZES, cardStatesParam } from "../../components/e-accreditation/model";
import { BatchKindChip, BatchStatusChip, BatchStatusGuide } from "../../components/e-accreditation/parts";
import {
	invalidateEAccreditation,
	markPrintBatchPrinted,
	markPrintBatchReceived,
	printBatchOptions,
	printBatchesOptions,
	sendPrintBatchToPrinter,
} from "../../lib/api/e-accreditation";
import { useDefaultGamesId } from "../../lib/competition/games";
import { canActivateCards, canSeePrintBatches, isNocView } from "../../lib/e-accreditation/access";
import { formatDateTime, formatShort } from "../../lib/format/datetime";
import { keysetSearch } from "../../lib/list-page/list-search";
import { useListPage } from "../../lib/list-page/use-list-page";
import { toast } from "../../lib/ui/toast";

/**
 * Print batches (the print pipeline, 0075): the batches made from the Cards
 * list, newest first (keyset). A batch opens a sheet — its facts, the card
 * generator ("Generate Card", drawn on this computer), then each
 * production step the API allows (`GET /print-batches/:id` → `actions`):
 * sent to the printer → back from the printer → received, per organisation
 * as each NOC collects — and "Activate these cards". A NOC sees only the
 * self-print batches the host made for it (the API decides which), with the
 * generator and nothing else. `batch` in the URL opens one batch's sheet.
 */
export const Route = createFileRoute("/_app/accreditation/e-accreditation/print-batches")({
	validateSearch: keysetSearch({ games_id: z.string().uuid(), batch: z.string().uuid() }),
	beforeLoad: ({ context }) => {
		if (!canSeePrintBatches(context.session)) throw redirect({ to: "/accreditation/e-accreditation" });
	},
	component: PrintBatchesPage,
});

type Key = "name" | "kind" | "status" | "cards" | "created" | "generated" | "printed" | "notes";

function PrintBatchesPage() {
	const { t } = useTranslation("eAccreditation");
	const { formatNumber: fmt } = useUiStrings();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const { session } = Route.useRouteContext();
	const noc = isNocView(session);
	const gamesId = useDefaultGamesId(search.games_id);
	const page = useListPage({
		paging: "keyset",
		search,
		navigate,
		listOptions: (q) => printBatchesOptions({ games_id: gamesId ?? "", ...q }),
		columnsKey: "e-accreditation-print-batches",
		pageSizes: BATCH_PAGE_SIZES,
		defaultPageSize: PAGE_SIZE_DEFAULT,
		summary: {
			showing: (v) => t("batches.pager.showing", v),
			showingNoTotal: (v) => t("batches.pager.showingNoTotal", v),
			empty: t("batches.pager.empty"),
		},
	});
	const open = search.batch ?? null;
	const setOpen = (b: PrintBatchListItem | null) => void navigate({ search: (p) => ({ ...p, batch: b?.print_batch_id }), replace: true });

	const by = (who: { name: string } | null) => (who ? t("common.byName", { name: who.name }) : null);
	const stamp = (at: string | null, who: { name: string } | null) =>
		at ? (
			<span className="grid">
				<time dateTime={at} title={formatDateTime(at)} className="whitespace-nowrap text-gms-ink-2 tabular-nums">
					{formatShort(at)}
				</time>
				{who && <span className="text-[0.75rem] text-gms-ink-3">{by(who)}</span>}
			</span>
		) : (
			<span className="text-gms-ink-3">—</span>
		);
	const columns: ColumnDef<PrintBatchListItem, Key>[] = [
		{
			key: "name",
			header: t("batches.table.name"),
			lockVisible: true,
			minWidth: "12rem",
			cell: (b) => (
				<button type="button" onClick={() => setOpen(b)} className="cursor-pointer text-left font-semibold whitespace-nowrap text-gms-navy-ink tabular-nums hover:text-gms-navy">
					{b.name}
				</button>
			),
		},
		{
			key: "kind",
			header: t("batches.table.kind"),
			minWidth: "8rem",
			cell: (b) => (
				<span className="grid justify-items-start gap-[0.125rem]">
					<BatchKindChip kind={b.kind} />
					{b.organization && <span className="text-[0.75rem] text-gms-ink-3">{b.organization.name}</span>}
				</span>
			),
		},
		{ key: "status", header: t("batches.table.status"), minWidth: "7rem", cell: (b) => <BatchStatusChip status={b.status} /> },
		{ key: "cards", header: t("batches.table.cards"), numeric: true, minWidth: "5rem", cell: (b) => fmt(b.card_count) },
		{ key: "created", header: t("batches.table.created"), minWidth: "10rem", cell: (b) => stamp(b.created_at, b.created_by) },
		{ key: "generated", header: t("batches.table.generated"), minWidth: "10rem", cell: (b) => stamp(b.generated_at, b.generated_by) },
		{ key: "printed", header: t("batches.table.printed"), minWidth: "10rem", cell: (b) => stamp(b.printed_at, b.printed_by) },
		{ key: "notes", header: t("batches.table.notes"), minWidth: "10rem", cell: (b) => <span className="line-clamp-2 text-gms-ink-2">{b.notes ?? "—"}</span> },
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			<div className="flex flex-wrap items-center justify-between gap-[0.875rem]">
				<p className="max-w-[40rem] text-[0.8125rem] text-gms-ink-3">{noc ? t("batches.introNoc") : t("batches.intro")}</p>
				<BatchStatusGuide size="sm" />
			</div>
			<DataTable<PrintBatchListItem, Key>
				{...page.table}
				variant="list"
				columns={noc ? columns.filter((c) => c.key !== "notes" && c.key !== "printed") : columns}
				rowKey={(b) => b.print_batch_id}
				pager={(p) => (page.list.isError || (p === "bottom" && page.rows.length === 0) ? null : <KeysetPager {...page.pager} position={p} />)}
				rowActions={(b) => <IconButton label={t("batches.row.open", { name: b.name })} icon={<Icons.Eye />} variant="row" onClick={() => setOpen(b)} />}
				empty={noc ? { title: t("batches.emptyNoc.title"), description: t("batches.emptyNoc.description") } : { title: t("batches.empty.title"), description: t("batches.empty.description") }}
			/>
			{open && <BatchSheet batchId={open} gamesId={search.games_id} noc={noc} canActivate={canActivateCards(session)} onClose={() => setOpen(null)} />}
		</div>
	);
}

/** A step the sheet asks to confirm: send to the printer, back from the printer, or receive (all, or one organisation's). */
type Confirm = { kind: "send" } | { kind: "printed" } | { kind: "receive"; org: { id: string; name: string } | null; count: number };

function BatchSheet({ batchId, gamesId, noc, canActivate, onClose }: { batchId: string; gamesId: string | undefined; noc: boolean; canActivate: boolean; onClose: () => void }) {
	const { t } = useTranslation("eAccreditation");
	const { formatNumber: fmt } = useUiStrings();
	const q = useQuery(printBatchOptions(batchId));
	const [confirm, setConfirm] = useState<Confirm | null>(null);
	const [busy, setBusy] = useState(false);
	const [generating, setGenerating] = useState(false);
	const batch = q.data;
	const locked = busy || generating;

	const close = () => {
		if (locked) return;
		setConfirm(null);
		onClose();
	};

	if (!batch) {
		return (
			<Modal open onClose={close} size="md" title={t("batches.table.name")}>
				{q.isError ? (
					<p role="alert" className="text-[0.84375rem] text-gms-bad">
						{t("batches.empty.title")}
					</p>
				) : (
					<div className="skeleton h-[12rem] rounded-[0.625rem]" />
				)}
			</Modal>
		);
	}

	const toReceive = batch.organizations.reduce((n, o) => n + o.to_receive, 0);
	const step = async () => {
		if (!confirm) return;
		setBusy(true);
		try {
			if (confirm.kind === "send") {
				const r = await sendPrintBatchToPrinter(batch.print_batch_id);
				toast.success(t("batches.sendDone", { count: r.moved, n: fmt(r.moved), name: batch.name }));
			} else if (confirm.kind === "printed") {
				const r = await markPrintBatchPrinted(batch.print_batch_id);
				toast.success(t("batches.markDone", { count: r.moved, n: fmt(r.moved), name: batch.name }));
			} else {
				const r = await markPrintBatchReceived(batch.print_batch_id, confirm.org?.id);
				toast.success(t("batches.receive.done", { count: r.moved, n: fmt(r.moved) }));
			}
			await invalidateEAccreditation();
			setConfirm(null);
		} catch (err) {
			toast.error(err, confirm.kind === "send" ? t("batches.sendFailed") : confirm.kind === "printed" ? t("batches.markFailed") : t("batches.receive.failed"));
		} finally {
			setBusy(false);
		}
	};

	const primary = "btn btn-sm border-gms-navy bg-gms-navy text-white hover:bg-gms-navy-lift";
	const fact = (label: string, value: ReactNode, sub?: string | null) => (
		<div className="grid gap-[0.125rem] border-t border-gms-line px-[0.875rem] py-[0.5rem] first:border-t-0 min-[30rem]:grid-cols-[9rem_minmax(0,1fr)]">
			<dt className="text-[0.8125rem] text-gms-ink-3">{label}</dt>
			<dd className="text-[0.84375rem] text-gms-navy-ink">
				{value}
				{sub && <span className="block text-[0.75rem] text-gms-ink-3">{sub}</span>}
			</dd>
		</div>
	);
	// The one button for the step the batch is at (the "Next step" box); none when this login may not take it.
	const flowStep = batchFlow(batch);
	const nextAction = (() => {
		const s = flowStep.steps[flowStep.current];
		if (s === "send" && batch.actions.send_to_printer)
			return (
				<button type="button" className={primary} disabled={locked} onClick={() => setConfirm({ kind: "send" })}>
					{t("batches.send")}
				</button>
			);
		if (s === "back" && batch.actions.mark_printed)
			return (
				<button type="button" className={primary} disabled={locked} onClick={() => setConfirm({ kind: "printed" })}>
					{t("batches.markPrinted")}
				</button>
			);
		if (s === "receive" && batch.actions.mark_received && toReceive > 0)
			return (
				<button type="button" className={primary} disabled={locked} onClick={() => setConfirm({ kind: "receive", org: null, count: toReceive })}>
					{t("batches.receive.markAll")}
				</button>
			);
		if ((s === "activate" || (s === "receive" && batch.kind === "HOST")) && canActivate && (batch.status === "PRINTED" || (batch.kind === "SELF_PRINT" && batch.status === "GENERATED")))
			return (
				<Link
					to="/accreditation/e-accreditation/cards"
					search={{ games_id: gamesId, print_batch_id: batch.print_batch_id, card_state: cardStatesParam(CARD_ACTIVATABLE_STATES) }}
					className={s === "activate" ? primary : "btn btn-sm border-gms-line bg-base-100 text-gms-navy"}
					disabled={locked}
				>
					{t("batches.activate")}
				</Link>
			);
		return null;
	})();
	const when = (at: string | null) => (at ? formatShort(at) : t("common.notYet"));
	const by = (who: { name: string } | null) => (who ? t("common.byName", { name: who.name }) : null);

	const confirmText = (c: Confirm): { title: string; body: string; button: string } => {
		if (c.kind === "send") return { title: t("batches.sendTitle", { name: batch.name }), body: t("batches.sendBody", { count: batch.card_count, n: fmt(batch.card_count) }), button: t("batches.send") };
		if (c.kind === "printed")
			return { title: t("batches.markTitle", { name: batch.name }), body: t("batches.markBody", { count: batch.card_count, n: fmt(batch.card_count) }), button: t("batches.markConfirm") };
		return {
			title: c.org ? t("batches.receive.confirmTitle", { name: c.org.name }) : t("batches.receive.confirmAllTitle", { batch: batch.name }),
			body: t("batches.receive.confirmBody", { count: c.count, n: fmt(c.count) }),
			button: c.org ? t("batches.receive.markOne") : t("batches.receive.markAll"),
		};
	};

	return (
		<Modal
			open
			onClose={close}
			dismissible={!locked}
			size="lg"
			title={batch.name}
			description={
				<span className="inline-flex flex-wrap gap-[0.375rem]">
					<BatchStatusChip status={batch.status} />
					<BatchKindChip kind={batch.kind} />
				</span>
			}
			footer={
				confirm ? (
					<ModalActions>
						<button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirm(null)} disabled={busy}>
							{t("common.cancel")}
						</button>
						<button type="button" className={primary} disabled={busy} onClick={() => void step()}>
							{busy && <span className="loading loading-spinner loading-xs" />}
							{confirmText(confirm).button}
						</button>
					</ModalActions>
				) : (
					<ModalActions
						start={
							<Link
								to="/accreditation/e-accreditation/cards"
								search={{ games_id: gamesId, print_batch_id: batch.print_batch_id }}
								className="btn btn-ghost btn-sm text-gms-navy"
								disabled={locked}
							>
								{t("batches.showCards")}
							</Link>
						}
					>
						{null}
					</ModalActions>
				)
			}
		>
			{confirm ? (
				<div className="grid gap-[0.5rem]">
					<b className="text-[0.9375rem] font-semibold text-gms-navy-ink">{confirmText(confirm).title}</b>
					<p className="text-[0.84375rem] text-gms-ink-2">{confirmText(confirm).body}</p>
				</div>
			) : (
				<div className="grid gap-[0.875rem]">
					<BatchFlowTracker batch={batch} noc={noc} action={nextAction} />

					<dl className="overflow-hidden rounded-[0.625rem] border border-gms-line">
						{fact(t("batches.table.cards"), fmt(batch.card_count))}
						{batch.organization && fact(t("batches.organisation"), batch.organization.name)}
						{fact(t("batches.table.created"), formatShort(batch.created_at), by(batch.created_by))}
						{fact(t("batches.table.generated"), when(batch.generated_at), by(batch.generated_by))}
						{batch.kind === "HOST" && fact(t("batches.table.sent"), when(batch.sent_at), by(batch.sent_by))}
						{batch.kind === "HOST" && fact(t("batches.table.printed"), when(batch.printed_at), by(batch.printed_by))}
						{batch.notes && fact(t("batches.table.notes"), batch.notes)}
					</dl>

					{batch.actions.generate && <CardGeneratorPanel batch={batch} onBusy={setGenerating} />}

					{batch.kind === "HOST" && batch.organizations.length > 0 && (
						<Organisations batch={batch} canReceive={batch.actions.mark_received} disabled={locked} onReceive={(org, count) => setConfirm({ kind: "receive", org, count })} />
					)}
				</div>
			)}
		</Modal>
	);
}

/** The batch's cards per organisation: how many are back and waiting, how many received — and "Mark received" as each NOC collects. */
function Organisations({
	batch,
	canReceive,
	disabled,
	onReceive,
}: {
	batch: PrintBatchDetail;
	canReceive: boolean;
	disabled: boolean;
	onReceive: (org: { id: string; name: string }, count: number) => void;
}) {
	const { t } = useTranslation("eAccreditation");
	const { formatNumber: fmt } = useUiStrings();
	return (
		<section aria-labelledby="batch-orgs" className="grid gap-[0.5rem]">
			<b id="batch-orgs" className="px-[0.25rem] text-[0.875rem] font-semibold text-gms-navy-ink">
				{t("batches.receive.title")}
			</b>
			<ul className="overflow-hidden rounded-[0.625rem] border border-gms-line">
				{batch.organizations.map((o) => (
					<li key={o.organization.id} className="flex flex-wrap items-center gap-x-[0.75rem] gap-y-[0.25rem] border-t border-gms-line px-[0.875rem] py-[0.5rem] first:border-t-0">
						<span className="min-w-0 flex-1">
							<b className="block truncate text-[0.84375rem] font-semibold text-gms-navy-ink">{o.organization.name}</b>
							<span className="text-[0.75rem] text-gms-ink-3 tabular-nums">
								{t("batches.receive.cards", { count: o.cards, n: fmt(o.cards) })}
								{o.to_receive > 0 && ` · ${t("batches.receive.toReceive", { n: fmt(o.to_receive) })}`}
								{o.received > 0 && ` · ${t("batches.receive.received", { n: fmt(o.received) })}`}
							</span>
						</span>
						{canReceive && o.to_receive > 0 && (
							<button type="button" className="btn btn-xs border-gms-line bg-base-100 text-gms-navy" disabled={disabled} onClick={() => onReceive(o.organization, o.to_receive)}>
								{t("batches.receive.markOne")}
							</button>
						)}
					</li>
				))}
			</ul>
		</section>
	);
}
